import { memo } from 'react';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { LocationTag, OSIcon } from '@investigator/shared/components';
import { EntityInformationField as EntityField } from '../../types';
import { SkeletonLoader } from '../SkeletonLoader';
import {
  MultiValueField,
  SingleValueField,
} from './EntityInformationFieldDisplay';

/**
 * Memoized skeleton loader that won't re-render and recalculate random widths
 */
const LoadingEntityInformationField = memo(() => (
  <SkeletonLoader rowCount={1} />
));

export interface EntityInformationFieldProps {
  field: EntityField;
  showLabel?: boolean;
  labelMinW?: string;
  isInline?: boolean;
  skeletonWidth?: string;
}

/**
 * Renders an entity information field with support for:
 * - Single value with truncation and tooltip
 * - Multi-value with "+N" indicator
 * - Location data with flag
 * - OS icons
 * - Loading skeleton state
 */
export const EntityInformationField = ({
  field,
  showLabel = true,
  labelMinW = '120px',
  isInline = false,
  skeletonWidth = '80px',
}: EntityInformationFieldProps): JSX.Element => {
  const renderInformationField = () => {
    if (field.isLoading) {
      return (
        <Box w={skeletonWidth}>
          <LoadingEntityInformationField />
        </Box>
      );
    }

    return (
      <HStack spacing={1}>
        {field.hasOsIcon && field.value && (
          <OSIcon os={field.value} boxSize={4} />
        )}
        {field.locationData ? (
          <LocationTag
            countryCode={field.locationData.countryCode}
            country={field.locationData.country}
            city={field.locationData.city}
          />
        ) : field.edrHosts || field.values ? (
          <MultiValueField values={field.values} edrHosts={field.edrHosts} />
        ) : (
          <SingleValueField value={field.value} />
        )}
      </HStack>
    );
  };

  if (isInline) {
    return (
      <HStack spacing={2}>
        {showLabel && (
          <Text textStyle="body-md" color="text.secondary" minW={labelMinW}>
            {field.label}
          </Text>
        )}
        {renderInformationField()}
      </HStack>
    );
  }

  return (
    <VStack align="start" spacing={0}>
      {showLabel && (
        <Text textStyle="body-md" color="text.secondary">
          {field.label}
        </Text>
      )}
      {renderInformationField()}
    </VStack>
  );
};
